import axios from 'axios'
import { APIResponse } from '../types'
import { mockBlogPostsResponse, mockContentPages, mockBlogPosts } from '../services/mockData'

// Check if we're in production and backend is not available
const isProduction = import.meta.env.PROD
const backendAvailable = !isProduction // Assume backend is only available in development

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api',
  timeout: 60000, // 60 seconds for AI generation requests
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens or other headers here if needed
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 404) {
      console.error('API endpoint not found:', error.config?.url)
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.data?.error || error.message)
    }
    return Promise.reject(error)
  }
)

export default api

// API helper functions
export const apiRequest = async <T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any
): Promise<APIResponse<T>> => {
  // In production, return mock data for certain endpoints
  if (!backendAvailable) {
    return handleMockRequest<T>(method, url, data)
  }

  try {
    const response = await api.request({ method, url, data })

    // Normalize all successful responses to the APIResponse<T> shape
    const payload = response.data

    // If backend already includes { success, ... }, extract and wrap the rest as data
    if (payload && typeof payload === 'object') {
      const successFlag = Object.prototype.hasOwnProperty.call(payload, 'success')
        ? Boolean((payload as any).success)
        : true

      const { success: _s, error: backendError, message, ...rest } = payload as any

      const normalized: APIResponse<T> = {
        success: successFlag,
        data: rest as T,
      }
      if (backendError) normalized.error = String(backendError)
      if (message) normalized.message = String(message)
      return normalized
    }

    // Fallback for non-object payloads
    return { success: true, data: payload as T }
  } catch (error: any) {
    // If backend is not available, fallback to mock data
    if (error?.code === 'ECONNREFUSED' || error?.response?.status === 404) {
      return handleMockRequest<T>(method, url, data)
    }

    return {
      success: false,
      error: error?.response?.data?.error || error.message || 'An error occurred',
    }
  }
}

// Mock request handler for production
const handleMockRequest = async <T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any
): Promise<APIResponse<T>> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 100))

  // Blog endpoints
  if (url.startsWith('/blog/posts')) {
    // Single post by slug
    if (url.startsWith('/blog/posts/') && method === 'GET') {
      // Extract clean slug (strip query/hash) and decode
      const raw = url.replace('/blog/posts/', '')
      let clean = decodeURIComponent(raw.split('?')[0].split('#')[0]).trim()
      // Normalize slug variations
      clean = clean.replace(/\/$/, '')        // trailing slash
                   .replace(/-\d+$/, '')      // trailing numeric suffix like -1

      // Try exact match first
      let post = mockBlogPosts.find(p => p.slug === clean)

      // Fallbacks: try without any remaining trailing slash again (defensive)
      if (!post) {
        const alt = clean.replace(/\/$/, '')
        post = mockBlogPosts.find(p => p.slug === alt)
      }

      if (post) {
        return { success: true, data: { post } as T }
      }
      return { success: false, error: 'Post not found' }
    }

    // List posts
    if (method === 'GET') {
      return { success: true, data: mockBlogPostsResponse as T }
    }
  }

  // Categories
  if (url === '/blog/categories' && method === 'GET') {
    const categories = Array.from(new Set(mockBlogPosts.map(p => p.category).filter(Boolean))) as string[]
    return { success: true, data: { categories } as T }
  }

  // Tags
  if (url === '/blog/tags' && method === 'GET') {
    const tags = Array.from(new Set(mockBlogPosts.flatMap(p => p.tags || [])))
    return { success: true, data: { tags } as T }
  }

  // Featured posts
  if (url.startsWith('/blog/featured') && method === 'GET') {
    const urlObj = new URL('https://example.com' + url)
    const limit = parseInt(urlObj.searchParams.get('limit') || '5', 10)
    const posts = mockBlogPosts.filter(p => p.featured).slice(0, limit)
    return { success: true, data: { posts } as T }
  }

  // Content pages
  if (url.startsWith('/content/pages')) {
    // List all pages
    if (url === '/content/pages' && method === 'GET') {
      const pages = Object.entries(mockContentPages).map(([slug, page]) => ({
        slug,
        title: page.title,
        meta_description: page.meta_description,
      }))
      return { success: true, data: { pages } as T }
    }

    // Single page by slug
    const slug = url.replace('/content/pages/', '')
    const page = mockContentPages[slug]
    if (page) {
      return { success: true, data: { page } as T }
    }
  }

  // For generator and verification endpoints, return appropriate responses
  if (url.includes('/generator/generate')) {
    return {
      success: true,
      data: {
        content: generateMockLLMsTxt(data),
        message: 'File generated successfully'
      } as T
    }
  }

  // Verification endpoints
  if (url.includes('/verification/check') || url.includes('/verification/verify')) {
    const testUrl = data?.url || 'https://example.com/llm.txt'
    return {
      success: true,
      data: {
        url: testUrl,
        accessible: true,
        valid_format: true,
        issues: [],
        suggestions: [
          'Your llm.txt file is well-structured and follows best practices!'
        ],
        content: `# llm.txt Generator - Free AI SEO Tool & Resource Hub

> Free llm.txt generator and comprehensive AI SEO resource hub. Create properly formatted llm.txt files to help AI tools like ChatGPT, Claude, and Gemini better understand and cite your website content.

## Core Tools & Services

- [llm.txt Generator](https://llmtext.net/generator) - Free tool to create properly formatted llm.txt files in minutes with guided setup and validation
- [File Verification Tool](https://llmtext.net/verification) - Check if your llm.txt file is accessible, properly formatted, and follows best practices
- [Homepage](https://llmtext.net/) - Main landing page with overview of tools, resources, and getting started guide

## Educational Content & Guides

- [What is llm.txt?](https://llmtext.net/what-is-llm-txt) - Complete guide to understanding the llm.txt standard and why your website needs one
- [Benefits of llm.txt](https://llmtext.net/benefits) - Discover key benefits including improved AI citations, better discoverability, and competitive advantage
- [Implementation Guide](https://llmtext.net/implementation-guide) - Step-by-step instructions for all platforms and technical skill levels
- [AI SEO Best Practices](https://llmtext.net/ai-seo-best-practices) - Master AI SEO with proven strategies for optimizing content for AI-powered search
- [Risks Without llm.txt](https://llmtext.net/risks-without-llm-txt) - Understand missed opportunities and competitive disadvantages of not implementing llm.txt

## Blog & Expert Insights

- [AI SEO Blog](https://llmtext.net/blog) - Latest insights, tips, and strategies for AI optimization and llm.txt implementation
- [What Is /llms.txt? The Definitive Guide](https://llmtext.net/blog/what-is-llms-txt-definitive-guide) - Comprehensive 3000+ word guide covering everything about the llm.txt standard
- [How to Create Your First /llms.txt in 5 Minutes](https://llmtext.net/blog/create-first-llms-txt-5-minutes) - Quick start tutorial with step-by-step instructions for beginners
- [Case Studies: Real /llms.txt Success Stories](https://llmtext.net/blog/llms-txt-case-studies-success-stories) - Real-world results showing 300%+ increases in AI citations and business growth
- [The Future of /llms.txt: AI Search Evolution](https://llmtext.net/blog/future-llms-txt-ai-search-evolution) - Industry trends, predictions, and strategic insights for the AI-first web
- [WordPress /llms.txt Plugin: Complete Setup Guide](https://llmtext.net/blog/wordpress-llms-txt-plugin-setup-guide) - Platform-specific implementation for WordPress sites with plugin and manual methods
- [Shopify /llms.txt Implementation: E-commerce AI SEO](https://llmtext.net/blog/shopify-llms-txt-implementation-ecommerce) - E-commerce optimization strategies for Shopify stores
- [Common /llms.txt Mistakes and How to Fix Them](https://llmtext.net/blog/common-llms-txt-mistakes-how-to-fix) - Top 10 implementation errors and troubleshooting guide

## Platform-Specific Resources

- [WordPress Implementation](https://llmtext.net/blog/wordpress-llms-txt-plugin-setup-guide) - Complete WordPress setup with plugin recommendations and manual methods
- [Shopify E-commerce Guide](https://llmtext.net/blog/shopify-llms-txt-implementation-ecommerce) - E-commerce specific strategies for product optimization
- [Static Site Implementation](https://llmtext.net/implementation-guide) - Instructions for Next.js, Astro, Hugo, and other static site generators

## Technical Documentation

- [File Format Specification](https://llmtext.net/what-is-llm-txt) - Official format guidelines, syntax rules, and structure requirements
- [Validation Guidelines](https://llmtext.net/verification) - Technical requirements for proper file accessibility and formatting
- [Best Practices Checklist](https://llmtext.net/ai-seo-best-practices) - Comprehensive optimization checklist for maximum AI visibility

## Success Stories & Case Studies

- [TechStartup Inc. Case Study](https://llmtext.net/blog/llms-txt-case-studies-success-stories) - 340% increase in AI citations and $2.3M in attributed revenue
- [HealthWise Clinic Success](https://llmtext.net/blog/llms-txt-case-studies-success-stories) - Local healthcare provider breakthrough with 280% visibility increase
- [EcoProducts E-commerce](https://llmtext.net/blog/llms-txt-case-studies-success-stories) - 425% increase in product mentions and $1.8M additional revenue
- [LegalAdvice Pro Results](https://llmtext.net/blog/llms-txt-case-studies-success-stories) - 298% increase in legal citations and 67% more qualified leads

## Company Information

- [About Us](https://llmtext.net/about) - Our mission to democratize AI optimization and make llm.txt accessible to everyone
- [Contact](https://llmtext.net/contact) - Get in touch with our team for support, questions, or partnership opportunities`
      } as T
    }
  }

  if (url.includes('/verification/validate')) {
    return {
      success: true,
      data: {
        url: 'content-validation',
        accessible: true,
        valid_format: true,
        issues: [],
        suggestions: [
          'Content structure looks good',
          'Consider adding section descriptions',
          'Ensure all URLs are absolute'
        ]
      } as T
    }
  }

  // Default fallback
  return {
    success: false,
    error: 'Endpoint not available in production mode'
  }
}

// Generate a mock /llms.txt file
const generateMockLLMsTxt = (formData: any): string => {
  const { website_name, website_summary, detailed_info } = formData || {}

  let content = `# ${website_name || 'Your Website'}\n\n`
  content += `> ${website_summary || 'Brief description of your website'}\n\n`

  if (detailed_info && detailed_info.length > 0) {
    detailed_info.forEach((section: any) => {
      if (section.section_name && section.urls && section.urls.length > 0) {
        content += `## ${section.section_name}\n\n`
        section.urls.forEach((urlItem: any) => {
          const note = urlItem.note ? ` - ${urlItem.note}` : ''
          content += `- [${urlItem.title}](${urlItem.url})${note}\n`
        })
        content += '\n'
      }
    })
  }

  return content
}

export const get = <T = any>(url: string): Promise<APIResponse<T>> =>
  apiRequest<T>('GET', url)

export const post = <T = any>(url: string, data?: any): Promise<APIResponse<T>> =>
  apiRequest<T>('POST', url, data)

export const put = <T = any>(url: string, data?: any): Promise<APIResponse<T>> =>
  apiRequest<T>('PUT', url, data)

export const del = <T = any>(url: string): Promise<APIResponse<T>> =>
  apiRequest<T>('DELETE', url)
